import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';
import LeaveDaysModal from './LeaveDaysModal';
import { canSalesTarget } from './salesTargetAccess';
import './MtdDistributionTab.css';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const MONTHS = [
    { value: 1, label: 'January' },  { value: 2, label: 'February' },
    { value: 3, label: 'March' },    { value: 4, label: 'April' },
    { value: 5, label: 'May' },      { value: 6, label: 'June' },
    { value: 7, label: 'July' },     { value: 8, label: 'August' },
    { value: 9, label: 'September' },{ value: 10, label: 'October' },
    { value: 11, label: 'November' },{ value: 12, label: 'December' }
];

const fmt = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n ?? 0));
const fmt2 = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);
const pct = (n) => `${(n ?? 0).toFixed(2)}%`;

const MtdDistributionTab = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [organizationId, setOrganizationId] = useState('');
    const [year, setYear] = useState(CURRENT_YEAR);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [data, setData] = useState(null);
    const [showAllDays, setShowAllDays] = useState(false);
    // Branch whose leave-days calendar is open: { branchId, branchName, leaveDays }
    const [calendarBranch, setCalendarBranch] = useState(null);
    // Saving leave days is a PUT (edit or approve on the card); resetting is a
    // DELETE (delete). A view-only user gets neither button rather than a 403.
    const canEditLeaveDays = canSalesTarget(user, 'edit', 'approve');
    const canResetLeaveDays = canSalesTarget(user, 'delete');

    useEffect(() => {
        const loadOrgs = async () => {
            try {
                const res = await axiosInstance.get('/api/sales-target/organizations/tree');
                if (res.data.success) {
                    const list = [];
                    const walk = (nodes) => nodes.forEach(n => {
                        if (n.type === 'company') list.push(n);
                        if (n.children) walk(n.children);
                    });
                    walk(res.data.data || []);
                    setCompanies(list);
                    if (!organizationId && list.length > 0) {
                        setOrganizationId(list[0]._id);
                    }
                }
            } catch (e) { console.error(e); }
        };
        loadOrgs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = useCallback(async () => {
        if (!organizationId) return;
        try {
            setLoading(true);
            setError(null);
            const params = { organizationId, year, month };
            const res = await axiosInstance.get('/api/sales-target/analytics/mtd-distribution', { params });
            if (res.data.success) setData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [organizationId, year, month]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openCalendar = (bg) => {
        setCalendarBranch({
            branchId: bg.branchId,
            branchName: bg.branchName,
            leaveDays: bg.workingDays?.leaveDays || []
        });
    };

    const onCalendarSaved = async () => {
        setCalendarBranch(null);
        await fetchData();
    };

    const handleExport = async () => {
        if (!organizationId) return;
        try {
            const res = await axiosInstance.get('/api/sales-target/analytics/mtd-distribution/export-excel', {
                params: { organizationId, year, month }, responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `MTD_Distribution_${MONTHS[month - 1].label}_${year}.xlsx`;
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Failed to export to Excel');
        }
    };

    // Default view: show only days that ACTUALLY have data (never future days). This matches
    // the data-driven "elapsed" logic — today's column won't appear until tonight's upload
    // lands, and a missing day (e.g. an un-uploaded yesterday) simply isn't shown.
    // "Show all days" overrides this to display the full month.
    const visibleDays = data
        ? (showAllDays
            ? Array.from({ length: data.period.daysInMonth }, (_, i) => i + 1)
            : Array.from({ length: data.period.daysInMonth }, (_, i) => i + 1).filter(d => {
                if (d > data.period.daysGone) return false; // never show future days
                return data.branches.some(bg => bg.salesmen.some(s => (s.dailySales[d] || 0) > 0));
            }))
        : [];

    const renderRow = (label, row, type = 'salesman', leaveSet = null) => {
        // On-pace shading for the Achieved cell: green when the salesman has already sold
        // at least what they should have by now (perDayTarget × working days elapsed),
        // soft red when behind. Neutral when there's no pace to judge (no elapsed / no target).
        const hasPace = row.expectedToDate > 0;
        const paceClass = hasPace ? (row.achieved >= row.expectedToDate ? ' mtd-pace-ahead' : ' mtd-pace-behind') : '';
        const paceTitle = hasPace ? `Expected by now: ${fmt(row.expectedToDate)}` : undefined;
        // Per-day pace: green when the daily average sold (Per Day Ach) meets/beats the
        // required daily rate (Per Day Tgt). Same signal as above, shown where Tgt sits
        // right beside Ach so the comparison is self-evident.
        const perDayPaceClass = hasPace ? (row.perDayAchieved >= row.perDayTarget ? ' mtd-pace-ahead' : ' mtd-pace-behind') : '';
        return (
        <tr className={`mtd-row mtd-row-${type}`}>
            <td className="mtd-name">{label}</td>
            <td className="mtd-num">{fmt(row.target)}</td>
            <td className={`mtd-num mtd-achieved${paceClass}`} title={paceTitle}>{fmt2(row.achieved)}</td>
            <td className={`mtd-num mtd-pct ${row.achievementPct >= 100 ? 'good' : row.achievementPct >= 50 ? 'mid' : 'low'}`}>
                {pct(row.achievementPct)}
            </td>
            {/* Target pro-rated to the working days elapsed, and the resulting surplus
                or shortfall. Answers "are we where we should be by now?", which the
                full-month Ach % cannot — at 85% through the month a 83% Ach % is
                marginally behind, not a crisis. */}
            <td className="mtd-num mtd-todate" title={hasPace ? `Target for ${fmt(row.perDayTarget)}/day × working days elapsed` : undefined}>
                {hasPace ? fmt(row.expectedToDate) : '—'}
            </td>
            <td className={`mtd-num mtd-schedule${hasPace ? (row.achieved >= row.expectedToDate ? ' ahead' : ' behind') : ''}`}>
                {hasPace ? `${row.achieved >= row.expectedToDate ? '+' : '−'}${fmt(Math.abs(row.achieved - row.expectedToDate))}` : '—'}
            </td>
            {visibleDays.map(d => {
                const val = row.dailySales[d] || 0;
                const isLeave = leaveSet ? leaveSet.has(d) : false;
                const exceeds = !isLeave && row.perDayTarget > 0 && val > row.perDayTarget;
                return (
                    <td key={d} className={`mtd-num mtd-day${exceeds ? ' mtd-day-exceeded' : ''}${isLeave ? ' mtd-day-leave' : ''}`} title={isLeave ? 'Leave day (off)' : undefined}>
                        {val > 0 ? fmt(val) : (isLeave ? 'L' : '')}
                    </td>
                );
            })}
            <td className="mtd-num mtd-perday">{fmt(row.perDayTarget)}</td>
            <td className={`mtd-num mtd-perday${perDayPaceClass}`} title={hasPace ? `Target/day: ${fmt(row.perDayTarget)}` : undefined}>{fmt(row.perDayAchieved)}</td>
            <td className="mtd-num mtd-exceeded">{row.daysExceeded}</td>
            <td className="mtd-num mtd-balance">{fmt(row.balance)}</td>
            <td className="mtd-num mtd-required">{fmt(row.requiredPerDay)}</td>
        </tr>
        );
    };

    return (
        <div className="mtd-distribution-tab">
            <div className="mtd-controls">
                <div className="mtd-filter">
                    <label>Company</label>
                    <select value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
                        <option value="">-- Select Company --</option>
                        {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="mtd-filter">
                    <label>Year</label>
                    <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div className="mtd-filter">
                    <label>Month</label>
                    <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
                        {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                </div>
                <div className="mtd-filter">
                    <label>Days</label>
                    <label className="mtd-checkbox">
                        <input type="checkbox" checked={showAllDays} onChange={(e) => setShowAllDays(e.target.checked)} />
                        Show all days
                    </label>
                </div>
                <button className="mtd-btn-export" onClick={handleExport} disabled={loading || !data}>
                    📊 Export Excel
                </button>
                <button className="mtd-btn-refresh" onClick={fetchData} disabled={loading}>↻ Refresh</button>
            </div>

            {error && <div className="mtd-error">⚠ {error}</div>}

            {loading && <div className="mtd-loading">Loading…</div>}

            {data && !loading && (
                <>
                    <div className="mtd-header-bar">
                        <div className="mtd-title">
                            <span className="mtd-month">{data.period.monthName} {data.period.year}</span>
                            <span className="mtd-org">— {data.organization.name}</span>
                        </div>
                        <div className="mtd-meta">
                            <span>Calendar Days Gone: <strong>{data.period.daysGone}</strong></span>
                            <span>Calendar Days Left: <strong>{data.period.daysLeft}</strong></span>
                            <span>Total Calendar Days: <strong>{data.period.daysInMonth}</strong></span>
                            <span className="mtd-meta-hint">Leave days set per-branch below ↓</span>
                        </div>
                    </div>

                    <div className="mtd-table-wrap">
                        <table className="mtd-table">
                            <thead>
                                <tr>
                                    <th rowSpan={2} className="mtd-name">Salesman</th>
                                    <th rowSpan={2}>Target</th>
                                    <th rowSpan={2}>Achieved</th>
                                    <th rowSpan={2}>Ach %</th>
                                    <th rowSpan={2} title="Target pro-rated to the working days elapsed">Tgt to Date</th>
                                    <th rowSpan={2} title="Achieved minus target-to-date">vs Schedule</th>
                                    <th colSpan={visibleDays.length} className="mtd-day-group">Daily Sales</th>
                                    <th colSpan={2} className="mtd-perday-group">Per Day</th>
                                    <th rowSpan={2}>&gt; Trgt</th>
                                    <th rowSpan={2}>Balance</th>
                                    <th rowSpan={2}>Req/Day</th>
                                </tr>
                                <tr>
                                    {visibleDays.map(d => <th key={d} className="mtd-day-h">{d}</th>)}
                                    <th className="mtd-perday-h">Tgt</th>
                                    <th className="mtd-perday-h">Ach</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.branches.map(bg => {
                                    const leaveSet = new Set(bg.workingDays?.leaveDays || []);
                                    return (
                                    <React.Fragment key={bg.branchId}>
                                        <tr className="mtd-section">
                                            <td colSpan={6 + visibleDays.length + 5}>
                                                <div className="mtd-section-row">
                                                    <span className="mtd-section-name">
                                                        {bg.branchName}
                                                        {bg.managerNames && bg.managerNames.length > 0 && (
                                                            <span className="mtd-manager"> — {bg.managerNames.join(', ')}</span>
                                                        )}
                                                    </span>
                                                    <span className="mtd-branch-days">
                                                        Working Days: <strong>{bg.workingDays?.daysInMonth ?? data.period.daysInMonth}</strong>
                                                        {leaveSet.size > 0 && (
                                                            <span className="mtd-days-custom" title={`Calendar: ${bg.workingDays?.calendarDays} · ${leaveSet.size} leave day(s)`}>
                                                                {' '}({leaveSet.size} leave)
                                                            </span>
                                                        )}
                                                        {' '}· Gone: <strong>{bg.workingDays?.daysGone ?? data.period.daysGone}</strong>
                                                        {' '}· Left: <strong>{bg.workingDays?.daysLeft ?? data.period.daysLeft}</strong>
                                                        {' '}· Time: <strong>{((bg.workingDays?.timeGonePct ?? data.period.timeGonePct) * 100).toFixed(1)}%</strong>
                                                        {canEditLeaveDays && (
                                                            <button
                                                                className="mtd-days-btn mtd-leave-btn"
                                                                onClick={() => openCalendar(bg)}
                                                                title="Mark leave days for this branch"
                                                            >📅 Leave days</button>
                                                        )}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                        {bg.salesmen.length === 0 && (
                                            <tr><td colSpan={6 + visibleDays.length + 5} className="mtd-empty">
                                                No salesmen in this branch
                                            </td></tr>
                                        )}
                                        {bg.salesmen.map(s => (
                                            <React.Fragment key={s.salesmanId}>
                                                {renderRow(s.name, s, 'salesman', leaveSet)}
                                            </React.Fragment>
                                        ))}
                                        {bg.salesmen.length > 0 && renderRow(
                                            <>
                                                {`TOTAL: ${bg.branchName}`}
                                                {bg.overAllocatedTarget > 0.01 && (
                                                    <span
                                                        style={{ color: '#b91c1c', fontWeight: 600, marginLeft: 8, fontSize: '0.85em' }}
                                                        title={`Salesmen targets exceed the branch target (${fmt(bg.branchTarget)}) by ${fmt(bg.overAllocatedTarget)}. Re-split the target to reconcile.`}
                                                    >⚠ over-allocated</span>
                                                )}
                                                {bg.unallocatedTarget > 0.01 && (
                                                    <span
                                                        style={{ color: '#b45309', fontWeight: 600, marginLeft: 8, fontSize: '0.85em' }}
                                                        title={`${fmt(bg.unallocatedTarget)} of the branch target (${fmt(bg.branchTarget)}) is unallocated — every salesman already has an explicit target. Re-split to assign it.`}
                                                    >⚠ {fmt(bg.unallocatedTarget)} unallocated</span>
                                                )}
                                            </>,
                                            bg.subtotal,
                                            'subtotal',
                                            leaveSet
                                        )}
                                    </React.Fragment>
                                    );
                                })}

                                <tr className="mtd-spacer"><td colSpan={6 + visibleDays.length + 5}></td></tr>
                                {renderRow('GRAND TOTAL', data.grandTotal, 'grand')}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {calendarBranch && data && (
                <LeaveDaysModal
                    branchId={calendarBranch.branchId}
                    branchName={calendarBranch.branchName}
                    year={year}
                    month={month}
                    calendarDays={data.period.calendarDays}
                    initialLeaveDays={calendarBranch.leaveDays}
                    onClose={() => setCalendarBranch(null)}
                    onSaved={onCalendarSaved}
                    canReset={canResetLeaveDays}
                />
            )}

            {!data && !loading && !error && organizationId === '' && (
                <div className="mtd-info">Please select a company to view MTD distribution.</div>
            )}
        </div>
    );
};

export default MtdDistributionTab;
